'use client';
import { useEffect } from 'react';
import { PINNED_BOTTOM_HEIGHT_VAR } from '../utils/pinnedHeight';

/**
 * Bars pinned to the bottom edge sit on top of each other rather than stacking,
 * so what they cover is the tallest of the ones shown, not the sum.
 */
const shownBars = new Map<object, number>();

const publish = () => {
  const root = document.documentElement;
  const covered = Math.max(0, ...shownBars.values());

  if (covered > 0) {
    root.style.setProperty(PINNED_BOTTOM_HEIGHT_VAR, `${covered}px`);
  } else {
    root.style.removeProperty(PINNED_BOTTOM_HEIGHT_VAR);
  }
};

/**
 * Publishes the height of a bottom-pinned bar while it is on screen, as
 * `PINNED_BOTTOM_HEIGHT_VAR`, so the Sort & Filter button and rail can stay clear
 * of it. `node` is a state value (a callback ref), not a ref object, because the
 * bars render nothing until their data arrives and the hook has to see the node
 * appear. `shown` is false while the bar is slid off-screen.
 */
export const usePinnedBottomHeight = (
  node: HTMLElement | null,
  shown: boolean,
) => {
  useEffect(() => {
    if (!node || !shown) return;

    const key = {};
    const measure = () => {
      shownBars.set(key, node.offsetHeight);
      publish();
    };

    measure();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(measure);
    observer?.observe(node);

    return () => {
      observer?.disconnect();
      shownBars.delete(key);
      publish();
    };
  }, [node, shown]);
};
